import React, { useEffect, useState } from 'react';

import { Alert, AlertTitle, Avatar, Box, Card, CardContent, Dialog, Button, CircularProgress, Link, Typography } from "@mui/material";
import { useQuery } from "@apollo/client/react";
import { CombinedGraphQLErrors } from "@apollo/client/errors";
import { useMutation } from "react-query";

import App from "../App";
import Company from "../components/Company";
import Create from "../components/Create";
import OptionsMenu from "../components/OptionsMenu";
import { DASHBOARD_QUERY, DashboardData, toCompany } from '../graphql/operations';
import { CompanyData, PreviewData, UserData } from '../utils/types';
import { postLaunchPage } from '../utils/queries/launchPages';
import { deleteCompany, fetchNewLogo, fetchNewName, fetchNewTagline } from '../utils/queries/companies';
import { LoadingButton } from '@mui/lab';
import { useNavigate } from 'react-router-dom';

interface DashboardViewProps {
  handleSignIn: () => void;
  isFetchingJWT: boolean;
  isLoadingUserData: boolean;
  isSigningIn: boolean;
  setToast: ((toast?: { message: string, severity: string }) => void);
  userData?: UserData;
  userToken?: string;
}

const footerLinkSx = {
  color: "#5c574e",
  fontSize: 12,
  fontWeight: 600,
  letterSpacing: "0.16em",
  minWidth: 0,
  px: 0.5,
  textTransform: "uppercase",
  "&:hover": { bgcolor: "transparent", color: "#1c1916" },
};

const DashboardView = ({ handleSignIn, isFetchingJWT, isLoadingUserData, isSigningIn, setToast, userData }: DashboardViewProps) => {
  const navigate = useNavigate()

  const [selectedCompany, setSelectedCompany] = useState<string | undefined>();
  const [previewData, setPreviewData] = useState<PreviewData>({});
  const [isGenerating, setIsGenerating] = useState(false);

  const { data, loading, error, refetch: refetchCompanies } = useQuery<DashboardData>(DASHBOARD_QUERY, {
    skip: !userData,
    fetchPolicy: "cache-and-network",
  });

  const companyList = userData ? (data?.me?.companies ?? []) : [];
  const selected = companyList.find((company) => company.id === selectedCompany);
  const companyData = selected ? toCompany(selected) : undefined;
  const launchPageData = selected?.launchPage || undefined;
  const isLoading = loading && !data;
  const isLoadingCompanyData = Boolean(selectedCompany) && !companyData && (loading || !data);
  const isLoadingLaunchPageData = isLoadingCompanyData;

  useEffect(() => {
    if (!error) return;
    const message = CombinedGraphQLErrors.is(error) ? error.errors[0]?.message : error.message;
    if (message === "Unauthorized" || message === "jwt expired") return;
    setToast({
      message: "Error fetching company data",
      severity: "error",
    });
  }, [error, setToast]);

  const { mutate: generateNewLogo, isLoading: isGeneratingLogo } = useMutation<string, unknown, void, unknown>(() => fetchNewLogo(companyData, true), {
    onSuccess: () => {
      refetchCompanies();
    },
  });

  const { mutate: generateNewName, isLoading: isGeneratingName } = useMutation<string, unknown, string[], unknown>((excludedNames: string[]) => fetchNewName(undefined, companyData, true, excludedNames), {
    onSuccess: () => {
      refetchCompanies();
    },
  });

  const { mutate: generateNewTagline, isLoading: isGeneratingTagline } = useMutation<string, unknown, string[], unknown>((excludedTaglines) => fetchNewTagline(companyData, true, excludedTaglines), {
    onSuccess: () => {
      refetchCompanies();
    },
  });

  const { mutate: handleDeleteCompany, isLoading: isDeletingCompany } = useMutation(deleteCompany, {
    onSuccess: () => {
      if (!isDeletingCompany) refetchCompanies();
    },
  });

  const { mutate: createLaunchPage, isLoading: isCreatingLaunchPage } = useMutation((companyData: CompanyData) => postLaunchPage({
    slug: companyData.slug,
    companyId: companyData._id,
  }), {
    onSuccess: res => {
      navigate(`/${res.launchPage.slug}`);
    }
  });

  useEffect(() => {
    if (selectedCompany && previewData.id) setPreviewData({});
  }, [selectedCompany, previewData])

  const companyCard = (
    <Card elevation={0} sx={{ height: "100%", borderRadius: "8px", width: "100%" }}>
      <CardContent sx={{ height: "100%" }}>
        <Box display="flex" height="100%" flexDirection="column" alignItems="center" justifyContent="space-between" gap={1} mt={1}>
          <Company
            generateNewLogo={generateNewLogo}
            generateNewName={generateNewName}
            generateNewTagline={generateNewTagline}
            companyData={previewData.id ? previewData : (companyData?._id === selectedCompany ? companyData : undefined)}
            isLoadingCompanyData={isLoadingCompanyData}
            isGeneratingLogo={isGeneratingLogo || Boolean(previewData?.id && !previewData.logo)}
            isGeneratingDescription={Boolean(previewData?.id && !previewData.description)}
            isGeneratingName={isGeneratingName || Boolean(previewData?.id && !previewData.name)}
            isGeneratingTagline={isGeneratingTagline || Boolean(previewData?.id && !previewData.tagline)}
            hasLaunched={isLoadingLaunchPageData || Boolean(launchPageData)}
          />
          
          <Box display="flex" width="100%" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2} borderTop={(theme) => `1px solid ${theme.palette.divider}`} pt={3} sx={{ opacity: selectedCompany ? 1 : 0 }}>
            <Button variant='outlined' href={`/${companyData?._id}/roles`}>Business Dashboard</Button>
            {!launchPageData && !isLoadingLaunchPageData && (
              <>
                {selectedCompany && companyData?.slug && <LoadingButton variant="outlined" loading={isCreatingLaunchPage} sx={{ textTransform: "none" }} onClick={() => createLaunchPage(companyData)}>{`Launch https://venturify.xyz/${companyData.slug}`}</LoadingButton>}
              </>
            )}
            {isLoadingLaunchPageData && <CircularProgress />}
            {Boolean(launchPageData) && (
              <Box>
                <Box display="flex" gap={1}>
                  <Typography>Launch page:</Typography>
                  <Link target="_blank" href={launchPageData?.slug}><Typography>{`https://venturify.xyz/${launchPageData?.slug}`}</Typography></Link>
                </Box>
                <Typography>{`Views: ${launchPageData?.pageViews}`}</Typography>
                <Typography>{`Waitlist length: ${launchPageData?.waitlist.length}`}</Typography>
              </Box>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <>
      <Box display="flex" flexDirection="column" sx={{ height: "100%", minHeight: 0 }}>
        <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", pt: { xs: 1, md: 3 }, pb: 2 }}>
          <Box sx={{ width: "100%", maxWidth: 680 }}>
            <Typography
              component="h1"
              sx={{
                fontFamily: '"Montserrat", sans-serif',
                fontWeight: 700,
                fontSize: { xs: "42px", sm: "56px", md: "64px" },
                lineHeight: 1.05,
                letterSpacing: "-0.03em",
                color: "#1c1916",
                m: 0,
                textAlign: "center",
              }}
            >
              Turn your ideas into a real landing page.
            </Typography>
          </Box>

          <Create
            handleOnSuccess={newId => {
              setIsGenerating(false);
              refetchCompanies();
              setSelectedCompany(newId);
            }}
            hasCompany={Boolean(companyList.length)}
            onError={(message) => {
              setIsGenerating(false);
              setToast({ message, severity: "error" });
            }}
            isFetchingJWT={isFetchingJWT}
            isGenerating={isGenerating}
            onSubmit={() => {
              setSelectedCompany(undefined);
              setIsGenerating(true);
            }}
            previewData={previewData}
            isLoadingUserData={isLoadingUserData}
            onRequireAuth={handleSignIn}
            setPreviewData={setPreviewData}
            userData={userData}
          />

          {isLoading && <CircularProgress size={28} sx={{ mt: 3, color: "#1c1916" }} />}
          {Boolean(userData) && !userData?.grantedProducts.some((product: string) => product === 'venturify') && (
            <Alert severity="success" sx={{ mt: 2, maxWidth: 680, borderRadius: "8px" }}>
              <AlertTitle>Welcome to the free version!</AlertTitle>
              <Box display="flex" gap={1} sx={{ flexDirection: { xs: "column", sm: "row" } }}>
                <Typography>You can create up to 1 company.</Typography>
                <Link href="/about"><Typography>Learn more</Typography></Link>
              </Box>
            </Alert>
          )}
          {!userData && isSigningIn && (
            <Alert severity="info" sx={{ mt: 2, maxWidth: 680, borderRadius: "8px" }}>
              <AlertTitle>Sign-in in progress</AlertTitle>
              <Typography>Finish signing in from the popup in order to join the waitlist.</Typography>
            </Alert>
          )}

          {Boolean(userData) && Boolean(companyList.length) && (
            <Box sx={{ width: "100%", maxWidth: 680, mt: { xs: 4, sm: 5 } }}>
              <Typography
                component="h2"
                sx={{
                  fontFamily: '"Source Serif 4", "PTSerif", serif',
                  fontWeight: 600,
                  fontSize: { xs: 32, sm: 40 },
                  color: "#1c1916",
                  letterSpacing: "-0.02em",
                  mb: 1.75,
                }}
              >
                Companies
              </Typography>
              <Box display="flex" flexDirection="column" gap={1.5}>
                {!isLoading && companyList.map(({ id: companyId, logo, name }) => {
                  const avatarSrc = Array.isArray(logo) && logo.length ? logo[logo.length - 1] : undefined;

                  return (
                    <Box
                      key={companyId}
                      role="button"
                      tabIndex={isGenerating ? -1 : 0}
                      onClick={() => {
                        if (!isGenerating) setSelectedCompany(companyId);
                      }}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" && !isGenerating) setSelectedCompany(companyId);
                      }}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.75,
                        bgcolor: "#fff",
                        border: "1px solid #e3ddd4",
                        borderRadius: "8px",
                        pl: 1.5,
                        pr: 0.75,
                        py: 1.25,
                        cursor: isGenerating ? "default" : "pointer",
                        opacity: isGenerating ? 0.65 : 1,
                        "&:hover": isGenerating ? undefined : {
                          borderColor: "#c9c2b6",
                        },
                      }}
                    >
                      <Avatar
                        alt={name}
                        src={avatarSrc}
                        variant="rounded"
                        sx={{
                          height: 44,
                          width: 44,
                          flexShrink: 0,
                          borderRadius: "6px",
                          bgcolor: "#ebe6de",
                          color: "#1c1916",
                          fontWeight: 700,
                          "& .MuiAvatar-img": { objectFit: "contain", padding: "6px", boxSizing: "border-box" },
                        }}
                      >
                        {name?.[0]}
                      </Avatar>
                      <Typography sx={{ fontWeight: 600, fontSize: 16, color: "#1c1916", minWidth: 0 }} noWrap>
                        {name}
                      </Typography>
                      <OptionsMenu iconSx={{ fontSize: "22px", color: "#8a8478" }} menuItems={[
                          {
                            label: "Delete company",
                            onClick: () => {
                              setSelectedCompany(undefined);
                              handleDeleteCompany(companyId);
                            },
                            disabled: !userData?.grantedProducts.some((product: string) => product === 'venturify') && companyList.length
                          }
                        ]} sx={{ marginLeft: "auto" }}
                      />
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}
        </Box>
        <Box display="flex" alignItems="center" justifyContent="center" gap={1.25} py={2.5}>
          <Button variant="text" href="/about" sx={footerLinkSx}>About</Button>
          <Box component="span" sx={{ color: "#b3ab9e", fontSize: 12, lineHeight: 1 }}>•</Box>
          <Button variant="text" href="/privacy" sx={footerLinkSx}>Privacy</Button>
        </Box>
      </Box>
      <Dialog
        PaperProps={{
          elevation: 0,
          sx: {
            backgroundColor: "transparent",
            borderRadius: "8px",
            maxHeight: "calc(100vh - 48px)",
            overflow: "auto",
            width: { xs: "calc(100% - 24px)", sm: "min(880px, calc(100% - 48px))" },
            maxWidth: 880,
            minHeight: { xs: "70vh", sm: 520 },
            m: 2,
          },
        }}
        BackdropProps={{ sx: { backgroundColor: "rgba(28, 25, 22, 0.4)" } }}
        fullWidth
        maxWidth="md"
        open={Boolean(selectedCompany) || Boolean(previewData?.id)}
        onClose={() => {
          if (selectedCompany) setSelectedCompany(undefined);
        }}
      >
        {(Boolean(selectedCompany) || Boolean(previewData?.id)) && companyCard}
      </Dialog>
    </>
  )
};

const View = () => (
  <App scenic containerSx={{ overflow: "hidden" }} Component={DashboardView} />
)

export default View;